import moment from "moment";
import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import MessageThread from "./Ticket/MessageThread";
import MessageInput from "./Ticket/MessageInput";
import Timeline from "./Ticket/Timeline";

import { ArrowLeft, Check, CheckCircle } from "lucide-react";
import { useApi } from "../../../providers/ApiProvider";
import { useSingleSupportTicket } from "../../../composables/support-ticket/useSingleSupportTicket";
import NotFound from "../../../routes/not-found";
import StatusBadge from "../../../components/ui/StatusBadge";
import SelectField from "../../../components/form/select-field";
import { CustomOptions } from "../../../utils/options";
import DefaultModal from "../../../components/modal/default-modal";
import Button from "../../../components/form/button";
import { AuthUser } from "../../../lib/auth/auth-store";
import AuthLayout from "../../../components/layouts/auth";
import { strReplace } from "../../../lib/helper-support";
import ImagePreviewer from "../../../components/ui/image-previewer";

const SingleSupportTicket = () => {
  const [newMessage, setNewMessage] = useState("");
  const [activeAttachment, setActiveAttachment] = useState<string | null>(null);
  const api = useApi();
  const { uuid: user_uuid } = AuthUser();

  const { code } = useParams();

  const { data: ticket, isLoading } = useSingleSupportTicket({
    code: code as string,
    enabled: !!code,
  });

  if (isLoading) {
    return;
  }

  if (!ticket) {
    return <NotFound />;
  }
  const AssignUser = async () => {
    await api.POST({
      endpoint: "/supportTicket/assign",
      payload: {
        ticket_code: ticket.code,
        user_uuid,
      },
      refreshKeys: [
        `supportTicket`,
        `supportTicket/${code}`,
        `supportTicket/${code}/activity`,
      ],
    });
  };

  const MarkAsAsResolved = async () => {
    await api.POST({
      endpoint: `/supportTicket/${ticket.code}/markAsAsResolved`,
      refreshKeys: [
        `supportTicket`,
        `supportTicket/${code}`,
        `supportTicket/${code}/activity`,
      ],
    });
  };

  const ChangeStatus = async (status: string) => {
    await api.POST({
      endpoint: "/supportTicket/changeStatus",
      payload: {
        ticket_code: ticket.code,
        status,
      },
      refreshKeys: [
        `supportTicket`,
        `supportTicket/${code}`,
        `supportTicket/${code}/activity`,
      ],
    });
  };

  const SendMessage = async (message: string) => {
    const res = await api.POST({
      endpoint: `supportTicket/${ticket.code}/message`,
      payload: {
        ticket_code: ticket.code,
        message,
      },
      refreshKeys: [`supportTicket/${code}/activity`],
    });

    if (res.data) {
      setNewMessage(res.data);
    }
  };

  return (
    <AuthLayout>
      <div className="space-y-6">
        <div>
          <Link
            to="/support-ticket"
            className="themeTextMuted flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back to Tickets</span>
          </Link>
        </div>
        <div className="box-shadow p-5 rounded-2xl border border-gray-200">
          <div>
            <div className="md:flex justify-between items-center">
              <div>
                <div className="flex gap-2 flex-wrap">
                  <h1 className="font-semibold text-primary text-md">
                    {ticket.code}
                  </h1>
                  {!ticket.closed_at && <StatusBadge status="open" />}
                  <StatusBadge status={ticket.status} />
                </div>
                <div className="flex gap-2 flex-wrap themeTextMuted my-2">
                  <span>{ticket.customer} •</span>
                  <span>
                    Tranaction ID: {ticket.external_reference || "N/A"}
                  </span>
                  <span>Created {moment(ticket.created_at).format("lll")}</span>
                </div>
              </div>
              <div className="md:w-1/4">
                {!ticket.closed_at && (
                  <SelectField
                    value={ticket.status}
                    onChangeInput={(value) => ChangeStatus(value)}
                    placeholder="Select status"
                    options={CustomOptions([
                      "pending_review",
                      "in_review",
                      "awaiting_customer_response",
                    ])}
                  />
                )}
              </div>
            </div>
            {!ticket.closed_at && (
              <>
                <hr className="my-3 border-0.5 border-gray-500/10" />
                <div className="flex gap-2 items-center">
                  <DefaultModal
                    modalButtonTriggerText={
                      <Button
                        text={
                          <p className="flex items-center gap-2">
                            <CheckCircle className="w-4 h-4" />
                            <span>Mark as resolved</span>
                          </p>
                        }
                        design="ghost"
                        className="bg-green-500/10 text-green-500 cursor-pointer px-3 py-1.5 text-xs font-medium rounded-full hover:bg-green-500 hover:text-black"
                      />
                    }
                    description="Are you sure you want to mark this ticket as resolved?"
                    onConfirmationAction={MarkAsAsResolved}
                  />
                  {!ticket.assigned_to && (
                    <DefaultModal
                      modalButtonTriggerText={
                        <Button
                          text={
                            <p className="flex items-center gap-2">
                              <Check className="w-4 h-4" />
                              <span>Assign to self</span>
                            </p>
                          }
                          design="ghost"
                          className="bg-gray-500/10 text-gray-500 cursor-pointer px-3 py-1.5 text-xs font-medium rounded-full hover:bg-gray-500 hover:text-white"
                        />
                      }
                      description="Are you sure you want to assign this ticket to self?"
                      onConfirmationAction={AssignUser}
                    />
                  )}
                </div>
              </>
            )}
          </div>
          {ticket.customer_data && (
            <>
              <hr className="my-3 border-0.5 border-gray-500/10" />
              <div className="themeTextMuted grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <div>FullName</div>
                  <div className="capitalize text-black mt-0.1">
                    {ticket.customer_data.first_name}{" "}
                    {ticket.customer_data.last_name}
                  </div>
                </div>
                <div>
                  <div>Gender</div>
                  <div className="capitalize text-black mt-0.1">
                    {ticket.customer_data.gender}
                  </div>
                </div>
                <div>
                  <div>ctag</div>
                  <div className="text-black mt-0.1">
                    {ticket.customer_data.username}
                  </div>
                </div>
                <div>
                  <div>Email</div>
                  <div className="text-black mt-0.1">
                    {ticket.customer_data.email}
                  </div>
                </div>
                <div>
                  <div>Phone number</div>
                  <div className="text-black mt-0.1">
                    {ticket.customer_data.phone_number}
                  </div>
                </div>
                <div>
                  <div>Verification Stage</div>
                  <div className="capitalize text-black mt-0.1">
                    {strReplace(
                      ticket.customer_data.verification_stage ||
                        "verify_identity",
                      "_",
                      " ",
                    )}
                  </div>
                </div>
              </div>
            </>
          )}
          <hr className="my-3 border-0.5 border-gray-500/10" />
          <div>
            <label className="themeTextMuted">Description</label>
            <p>{ticket.description}</p>
          </div>
        </div>

        <div>
          <h2 className="font-semibold text-secondary text-lg">Attachments</h2>
          <div className="flex gap-4 mt-2">
            {ticket?.attachments?.length > 0 ? (
              ticket.attachments.map(
                (attachment: { url: string }, index: number) => (
                  <div>
                    <button
                      key={index}
                      onClick={() => setActiveAttachment(attachment.url)}
                      className="text-blue-500 hover:underline"
                    >
                      Attachment {index + 1}
                    </button>
                  </div>
                ),
              )
            ) : (
              <p className="themeTextMuted">No attachments</p>
            )}
          </div>

          <ImagePreviewer
            open={!!activeAttachment}
            onClose={() => setActiveAttachment(null)}
            image={activeAttachment as string}
          />
          {/* Fullscreen Modal */}
          {/* {activeAttachment && (
                        <div className="fixed inset-0 z-50 bg-black bg-opacity-90 flex items-center justify-center">

                       
                            <button
                                onClick={() => setActiveAttachment(null)}
                                className="absolute top-5 right-5 text-white text-3xl"
                            >
                                ✕
                            </button>

                          
                            <div className="w-full h-full flex items-center justify-center p-4">
                                {activeAttachment.match(/\.(jpeg|jpg|png|gif|webp)$/i) ? (
                                    <img
                                        src={activeAttachment}
                                        alt="Preview"
                                        className="max-h-full max-w-full object-contain"
                                    />
                                ) : (
                                    <iframe
                                        src={activeAttachment}
                                        title="Attachment Preview"
                                        className="w-full h-full"
                                    />
                                )}
                            </div>
                        </div>
                    )} */}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <MessageThread newMessage={newMessage} code={ticket.code} />
            {!ticket.closed_at && (
              <MessageInput ticket={ticket} onSend={SendMessage} />
            )}
          </div>
          <div>
            <Timeline code={ticket.code} />
          </div>
        </div>
      </div>
    </AuthLayout>
  );
};

export default SingleSupportTicket;
